/**
 * Friendly page-name suffix for the browser tab, derived from the route path.
 *
 * The tab reads "<venue> - <page>" (e.g. "Madruga Festival - My Account"). The
 * home route ("/") and the festival / record landing roots get NO suffix, just
 * the venue name, so the front page stays clean. Unmapped routes fall back to a
 * title-cased last segment, so a newly-added route always gets a sensible label
 * without needing a change here.
 */

// Static routes → the label shown after the venue name.
const NAMED: Record<string, string> = {
  "/": "",
  "/account": "My Account",
  "/profile": "Profile",
  "/preferences": "Preferences",
  "/notifications": "Notifications",
  "/my-tickets": "My Tickets",
  "/tickets": "Tickets",
  "/tiers": "Membership",
  "/trial": "Free Trial",
  "/rewards": "Rewards",
  "/benefits": "Benefits",
  "/bookings": "My Bookings",
  "/book": "Book",
  "/book-service": "Book",
  "/cart": "Basket",
  "/sell": "Sell",
  "/wishlist": "Wishlist",
  "/events": "What's On",
  "/whats-on": "What's On",
  "/lineup": "Line-up",
  "/info": "Info",
  "/menu": "Menu",
  "/noticeboard": "Noticeboard",
  "/sign-in": "Sign In",
  "/sign-up": "Sign Up",
  "/forgot-password": "Forgot Password",
  "/reset-password": "Reset Password",
  "/privacy": "Privacy",
  "/terms": "Terms",
};

// /admin/<section> → the section label; joined as "Admin · <label>".
const ADMIN: Record<string, string> = {
  analytics: "Analytics",
  benefits: "Benefits",
  branding: "Branding",
  calendar: "Calendar",
  events: "Events",
  festival: "Festival",
  inventory: "Inventory",
  loyalty: "Loyalty",
  menu: "Menu",
  noticeboard: "Noticeboard",
  notifications: "Notifications",
  offers: "Offers",
  orders: "Orders",
  revenue: "Revenue",
  scan: "Scan",
  services: "Services",
  storefront: "Storefront",
  subscribers: "Subscribers",
  tags: "Tags",
  team: "Team",
};

function titleCase(seg: string): string {
  return seg.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function pageSuffixFor(pathname: string): string {
  const p = (pathname || "/").replace(/\/+$/, "") || "/";

  // Admin section → "Admin · Orders" etc.
  if (p === "/admin") return "Admin";
  if (p.startsWith("/admin/")) {
    const seg = p.split("/")[2] ?? "";
    return "Admin · " + (ADMIN[seg] ?? titleCase(seg));
  }

  if (p in NAMED) return NAMED[p];

  // Dynamic detail routes (path params) → a stable generic label.
  if (p.startsWith("/events/")) return "Event";
  if (p.startsWith("/festival/")) return ""; // festival landing = venue name only
  if (p.startsWith("/records/")) return "Record";
  if (p.startsWith("/ticket/") || p.startsWith("/tickets/")) return "Ticket";
  if (p.startsWith("/t/")) return ""; // NFC / short-link → venue name only

  // Generic fallback: title-case the last non-param segment.
  const seg = p.split("/").filter((s) => s && !s.startsWith("$")).pop();
  return seg ? titleCase(seg) : "";
}

/** Compose the full tab title from a venue base name + current path. */
export function pageTitle(base: string, pathname: string): string {
  const suffix = pageSuffixFor(pathname);
  return suffix ? `${base} - ${suffix}` : base;
}
